import { ConfigApi as api } from './config-api';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    postAuthLogin: build.mutation<PostAuthLoginApiResponse, PostAuthLoginApiArg>({
      query: (queryArg) => ({ url: `/auth/login`, method: 'POST', body: queryArg.body }),
    }),
    postAuthRefresh: build.mutation<PostAuthRefreshApiResponse, PostAuthRefreshApiArg>({
      query: (queryArg) => ({ url: `/auth/refresh`, method: 'POST', body: queryArg.body }),
    }),
    postAuthForgotPassword: build.mutation<PostAuthForgotPasswordApiResponse, PostAuthForgotPasswordApiArg>({
      query: (queryArg) => ({ url: `/auth/forgot-password`, method: 'POST', body: queryArg.body }),
    }),
    postAuthResetPassword: build.mutation<PostAuthResetPasswordApiResponse, PostAuthResetPasswordApiArg>({
      query: (queryArg) => ({ url: `/auth/reset-password`, method: 'POST', body: queryArg.body }),
    }),
    postUsers: build.mutation<PostUsersApiResponse, PostUsersApiArg>({
      query: (queryArg) => ({ url: `/users`, method: 'POST', body: queryArg.body }),
    }),
    getUsersMe: build.query<GetUsersMeApiResponse, GetUsersMeApiArg>({
      query: () => ({ url: `/users/me` }),
    }),
    patchUsersMe: build.mutation<PatchUsersMeApiResponse, PatchUsersMeApiArg>({
      query: (queryArg) => ({ url: `/users/me`, method: 'PATCH', body: queryArg.body }),
    }),
    postUsersVerificationSend: build.mutation<PostUsersVerificationSendApiResponse, PostUsersVerificationSendApiArg>({
      query: () => ({ url: `/users/verification/send`, method: 'POST' }),
    }),
    postUsersVerificationVerify: build.mutation<
      PostUsersVerificationVerifyApiResponse,
      PostUsersVerificationVerifyApiArg
    >({
      query: (queryArg) => ({ url: `/users/verification/verify`, method: 'POST', body: queryArg.body }),
    }),
    postThingDestinationPayload: build.mutation<
      PostThingDestinationPayloadApiResponse,
      PostThingDestinationPayloadApiArg
    >({
      query: (queryArg) => ({ url: `/thing/destination/payload`, method: 'POST', body: queryArg.body }),
    }),
    postThingDestination: build.mutation<PostThingDestinationApiResponse, PostThingDestinationApiArg>({
      query: (queryArg) => ({
        url: `/thing/destination`,
        method: 'POST',
        params: {
          confirmationToken: queryArg.confirmationToken,
        },
      }),
    }),
    postLocations: build.mutation<PostLocationsApiResponse, PostLocationsApiArg>({
      query: (queryArg) => ({ url: `/locations`, method: 'POST', body: queryArg.body }),
    }),
    getLocations: build.query<GetLocationsApiResponse, GetLocationsApiArg>({
      query: (queryArg) => ({
        url: `/locations`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          parent_id: queryArg.parentId,
          tenant_id: queryArg.tenantId,
          include_sub_locations: queryArg.includeSubLocations,
        },
      }),
    }),
    postLocationsByParentIdSub: build.mutation<PostLocationsByParentIdSubApiResponse, PostLocationsByParentIdSubApiArg>(
      {
        query: (queryArg) => ({ url: `/locations/${queryArg.parentId}/sub`, method: 'POST', body: queryArg.body }),
      },
    ),
    getLocationsById: build.query<GetLocationsByIdApiResponse, GetLocationsByIdApiArg>({
      query: (queryArg) => ({
        url: `/locations/${queryArg.id}`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          include_sub_locations: queryArg.includeSubLocations,
        },
      }),
    }),
    patchLocationsById: build.mutation<PatchLocationsByIdApiResponse, PatchLocationsByIdApiArg>({
      query: (queryArg) => ({
        url: `/locations/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    deleteLocationsById: build.mutation<DeleteLocationsByIdApiResponse, DeleteLocationsByIdApiArg>({
      query: (queryArg) => ({ url: `/locations/${queryArg.id}`, method: 'DELETE' }),
    }),
    getDevices: build.query<GetDevicesApiResponse, GetDevicesApiArg>({
      query: (queryArg) => ({
        url: `/devices`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          location_id: queryArg.locationId,
          location_ids: queryArg.locationIds,
          include_sensors: queryArg.includeSensors,
          tenant_id: queryArg.tenantId,
        },
      }),
    }),
    postDevices: build.mutation<PostDevicesApiResponse, PostDevicesApiArg>({
      query: (queryArg) => ({
        url: `/devices`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getDevicesById: build.query<GetDevicesByIdApiResponse, GetDevicesByIdApiArg>({
      query: (queryArg) => ({
        url: `/devices/${queryArg.id}`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          include_sensors: queryArg.includeSensors,
          include_latest_data: queryArg.includeLatestData,
          tenant_id: queryArg.tenantId,
        },
      }),
    }),
    patchDevicesById: build.mutation<PatchDevicesByIdApiResponse, PatchDevicesByIdApiArg>({
      query: (queryArg) => ({
        url: `/devices/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    deleteDevicesById: build.mutation<DeleteDevicesByIdApiResponse, DeleteDevicesByIdApiArg>({
      query: (queryArg) => ({
        url: `/devices/${queryArg.id}`,
        method: 'DELETE',
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getDevicesByIdStats: build.query<GetDevicesByIdStatsApiResponse, GetDevicesByIdStatsApiArg>({
      query: (queryArg) => ({
        url: `/devices/${queryArg.id}/stats`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          hours: queryArg.hours,
          aggregation: queryArg.aggregation,
          tenant_id: queryArg.tenantId,
        },
      }),
    }),
    getDevicesByDeviceIdSensors: build.query<GetDevicesByDeviceIdSensorsApiResponse, GetDevicesByDeviceIdSensorsApiArg>(
      {
        query: (queryArg) => ({
          url: `/devices/${queryArg.deviceId}/sensors`,
          headers: {
            'x-tenant-id': queryArg['x-tenant-id'],
          },
        }),
      },
    ),
    postDevicesByDeviceIdSensors: build.mutation<
      PostDevicesByDeviceIdSensorsApiResponse,
      PostDevicesByDeviceIdSensorsApiArg
    >({
      query: (queryArg) => ({
        url: `/devices/${queryArg.deviceId}/sensors`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    patchDevicesByDeviceIdSensorsAndSensorId: build.mutation<
      PatchDevicesByDeviceIdSensorsAndSensorIdApiResponse,
      PatchDevicesByDeviceIdSensorsAndSensorIdApiArg
    >({
      query: (queryArg) => ({
        url: `/devices/${queryArg.deviceId}/sensors/${queryArg.sensorId}`,
        method: 'PATCH',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    deleteDevicesByDeviceIdSensorsAndSensorId: build.mutation<
      DeleteDevicesByDeviceIdSensorsAndSensorIdApiResponse,
      DeleteDevicesByDeviceIdSensorsAndSensorIdApiArg
    >({
      query: (queryArg) => ({
        url: `/devices/${queryArg.deviceId}/sensors/${queryArg.sensorId}`,
        method: 'DELETE',
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getExportSensorData: build.query<GetExportSensorDataApiResponse, GetExportSensorDataApiArg>({
      query: (queryArg) => ({
        url: `/export/sensor-data`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          device_ids: queryArg.deviceIds,
          sensor_types: queryArg.sensorTypes,
          location_id: queryArg.locationId,
          location_ids: queryArg.locationIds,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          format: queryArg.format,
        },
      }),
    }),
    getExportSensorAggregates: build.query<GetExportSensorAggregatesApiResponse, GetExportSensorAggregatesApiArg>({
      query: (queryArg) => ({
        url: `/export/sensor-aggregates`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          location_id: queryArg.locationId,
          location_ids: queryArg.locationIds,
          device_id: queryArg.deviceId,
          device_ids: queryArg.deviceIds,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          sensor_types: queryArg.sensorTypes,
          interval: queryArg.interval,
          format: queryArg.format,
          chart_type: queryArg.chartType,
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
    }),
    getExportSensorTimeSeries: build.query<GetExportSensorTimeSeriesApiResponse, GetExportSensorTimeSeriesApiArg>({
      query: (queryArg) => ({
        url: `/export/sensor-time-series`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          device_ids: queryArg.deviceIds,
          sensor_types: queryArg.sensorTypes,
          location_id: queryArg.locationId,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          interval: queryArg.interval,
          aggregation: queryArg.aggregation,
          format: queryArg.format,
          chart_type: queryArg.chartType,
          limit: queryArg.limit,
          tenant_id: queryArg.tenantId,
        },
      }),
    }),
    getAnalyticsDashboard: build.query<GetAnalyticsDashboardApiResponse, GetAnalyticsDashboardApiArg>({
      query: (queryArg) => ({
        url: `/analytics/dashboard`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          days: queryArg.days,
          location_id: queryArg.locationId,
          location_ids: queryArg.locationIds,
        },
      }),
    }),
    getAnalyticsSensorData: build.query<GetAnalyticsSensorDataApiResponse, GetAnalyticsSensorDataApiArg>({
      query: (queryArg) => ({
        url: `/analytics/sensor-data`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          location_id: queryArg.locationId,
          location_ids: queryArg.locationIds,
          device_id: queryArg.deviceId,
          device_ids: queryArg.deviceIds,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          sensor_types: queryArg.sensorTypes,
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
    }),
    getAnalyticsSensorAggregates: build.query<
      GetAnalyticsSensorAggregatesApiResponse,
      GetAnalyticsSensorAggregatesApiArg
    >({
      query: (queryArg) => ({
        url: `/analytics/sensor-aggregates`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          location_id: queryArg.locationId,
          location_ids: queryArg.locationIds,
          device_id: queryArg.deviceId,
          device_ids: queryArg.deviceIds,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          sensor_types: queryArg.sensorTypes,
          interval: queryArg.interval,
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
    }),
    getAnalyticsDeviceStatus: build.query<GetAnalyticsDeviceStatusApiResponse, GetAnalyticsDeviceStatusApiArg>({
      query: (queryArg) => ({
        url: `/analytics/device-status`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          location_id: queryArg.locationId,
          location_ids: queryArg.locationIds,
          status: queryArg.status,
          search: queryArg.search,
          limit: queryArg.limit,
          offset: queryArg.offset,
          sort_by: queryArg.sortBy,
          sort_dir: queryArg.sortDir,
        },
      }),
    }),
    getAnalyticsTimeSeries: build.query<GetAnalyticsTimeSeriesApiResponse, GetAnalyticsTimeSeriesApiArg>({
      query: (queryArg) => ({
        url: `/analytics/time-series`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          device_ids: queryArg.deviceIds,
          sensor_types: queryArg.sensorTypes,
          location_id: queryArg.locationId,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          interval: queryArg.interval,
          aggregation: queryArg.aggregation,
          limit: queryArg.limit,
          tenant_id: queryArg.tenantId,
        },
      }),
    }),
    getTenants: build.query<GetTenantsApiResponse, GetTenantsApiArg>({
      query: () => ({ url: `/tenants` }),
    }),
    postTenants: build.mutation<PostTenantsApiResponse, PostTenantsApiArg>({
      query: (queryArg) => ({ url: `/tenants`, method: 'POST', body: queryArg.body }),
    }),
    getTenantsById: build.query<GetTenantsByIdApiResponse, GetTenantsByIdApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.id}` }),
    }),
    patchTenantsById: build.mutation<PatchTenantsByIdApiResponse, PatchTenantsByIdApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.id}`, method: 'PATCH', body: queryArg.body }),
    }),
    deleteTenantsById: build.mutation<DeleteTenantsByIdApiResponse, DeleteTenantsByIdApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.id}`, method: 'DELETE' }),
    }),
    postTenantsByIdMembersInvite: build.mutation<
      PostTenantsByIdMembersInviteApiResponse,
      PostTenantsByIdMembersInviteApiArg
    >({
      query: (queryArg) => ({ url: `/tenants/${queryArg.id}/members/invite`, method: 'POST', body: queryArg.body }),
    }),
    getTenantsInvitationsVerify: build.query<GetTenantsInvitationsVerifyApiResponse, GetTenantsInvitationsVerifyApiArg>(
      {
        query: (queryArg) => ({
          url: `/tenants/invitations/verify`,
          params: {
            token: queryArg.token,
          },
        }),
      },
    ),
    postTenantsInvitationsAccept: build.mutation<
      PostTenantsInvitationsAcceptApiResponse,
      PostTenantsInvitationsAcceptApiArg
    >({
      query: (queryArg) => ({ url: `/tenants/invitations/accept`, method: 'POST', body: queryArg.body }),
    }),
    getTenantsByIdMembers: build.query<GetTenantsByIdMembersApiResponse, GetTenantsByIdMembersApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.id}/members` }),
    }),
    patchTenantsByTenantIdMembersAndMemberIdRole: build.mutation<
      PatchTenantsByTenantIdMembersAndMemberIdRoleApiResponse,
      PatchTenantsByTenantIdMembersAndMemberIdRoleApiArg
    >({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/members/${queryArg.memberId}/role`,
        method: 'PATCH',
        body: queryArg.body,
      }),
    }),
    deleteTenantsByTenantIdMembersAndMemberId: build.mutation<
      DeleteTenantsByTenantIdMembersAndMemberIdApiResponse,
      DeleteTenantsByTenantIdMembersAndMemberIdApiArg
    >({
      query: (queryArg) => ({ url: `/tenants/${queryArg.tenantId}/members/${queryArg.memberId}`, method: 'DELETE' }),
    }),
    postTenantsByTenantIdMembersAndMemberIdResetPassword: build.mutation<
      PostTenantsByTenantIdMembersAndMemberIdResetPasswordApiResponse,
      PostTenantsByTenantIdMembersAndMemberIdResetPasswordApiArg
    >({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/members/${queryArg.memberId}/reset-password`,
        method: 'POST',
      }),
    }),
    getAlertRules: build.query<GetAlertRulesApiResponse, GetAlertRulesApiArg>({
      query: (queryArg) => ({
        url: `/alert-rules`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          device_id: queryArg.deviceId,
          device_ids: queryArg.deviceIds,
          sensor_type: queryArg.sensorType,
          notification_event_type_id: queryArg.notificationEventTypeId,
          severity: queryArg.severity,
          status: queryArg.status,
          limit: queryArg.limit,
          offset: queryArg.offset,
          sort_by: queryArg.sortBy,
          sort_dir: queryArg.sortDir,
        },
      }),
    }),
    postAlertRules: build.mutation<PostAlertRulesApiResponse, PostAlertRulesApiArg>({
      query: (queryArg) => ({
        url: `/alert-rules`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    putAlertRulesByRuleId: build.mutation<PutAlertRulesByRuleIdApiResponse, PutAlertRulesByRuleIdApiArg>({
      query: (queryArg) => ({
        url: `/alert-rules/${queryArg.ruleId}`,
        method: 'PUT',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    deleteAlertRulesByRuleId: build.mutation<DeleteAlertRulesByRuleIdApiResponse, DeleteAlertRulesByRuleIdApiArg>({
      query: (queryArg) => ({
        url: `/alert-rules/${queryArg.ruleId}`,
        method: 'DELETE',
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getAlerts: build.query<GetAlertsApiResponse, GetAlertsApiArg>({
      query: (queryArg) => ({
        url: `/alerts`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          device_id: queryArg.deviceId,
          device_ids: queryArg.deviceIds,
          sensor_id: queryArg.sensorId,
          sensor_ids: queryArg.sensorIds,
          severity: queryArg.severity,
          status: queryArg.status,
          from_date: queryArg.fromDate,
          to_date: queryArg.toDate,
          include_rule: queryArg.includeRule,
          limit: queryArg.limit,
          offset: queryArg.offset,
          sort_by: queryArg.sortBy,
          sort_dir: queryArg.sortDir,
        },
      }),
    }),
    getAlertsHistory: build.query<GetAlertsHistoryApiResponse, GetAlertsHistoryApiArg>({
      query: (queryArg) => ({
        url: `/alerts/history`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          device_id: queryArg.deviceId,
          device_ids: queryArg.deviceIds,
          sensor_id: queryArg.sensorId,
          sensor_ids: queryArg.sensorIds,
          severity: queryArg.severity,
          status: queryArg.status,
          from_date: queryArg.fromDate,
          to_date: queryArg.toDate,
          include_rule: queryArg.includeRule,
          limit: queryArg.limit,
          offset: queryArg.offset,
          sort_by: queryArg.sortBy,
          sort_dir: queryArg.sortDir,
        },
      }),
    }),
    postAlertsByAlertIdAcknowledge: build.mutation<
      PostAlertsByAlertIdAcknowledgeApiResponse,
      PostAlertsByAlertIdAcknowledgeApiArg
    >({
      query: (queryArg) => ({
        url: `/alerts/${queryArg.alertId}/acknowledge`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    postAlertsByAlertIdResolve: build.mutation<PostAlertsByAlertIdResolveApiResponse, PostAlertsByAlertIdResolveApiArg>(
      {
        query: (queryArg) => ({
          url: `/alerts/${queryArg.alertId}/resolve`,
          method: 'POST',
          body: queryArg.body,
          headers: {
            'x-tenant-id': queryArg['x-tenant-id'],
          },
        }),
      },
    ),
    getWaitlistCount: build.query<GetWaitlistCountApiResponse, GetWaitlistCountApiArg>({
      query: () => ({ url: `/waitlist/count` }),
    }),
    postWaitlistJoin: build.mutation<PostWaitlistJoinApiResponse, PostWaitlistJoinApiArg>({
      query: (queryArg) => ({ url: `/waitlist/join`, method: 'POST', body: queryArg.body }),
    }),
    postAiChatbot: build.mutation<PostAiChatbotApiResponse, PostAiChatbotApiArg>({
      query: (queryArg) => ({
        url: `/ai/chatbot`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getAiChatbotHistory: build.query<GetAiChatbotHistoryApiResponse, GetAiChatbotHistoryApiArg>({
      query: (queryArg) => ({
        url: `/ai/chatbot/history`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
    }),
    postNotificationsChannels: build.mutation<PostNotificationsChannelsApiResponse, PostNotificationsChannelsApiArg>({
      query: (queryArg) => ({
        url: `/notifications/channels`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getNotificationsChannels: build.query<GetNotificationsChannelsApiResponse, GetNotificationsChannelsApiArg>({
      query: (queryArg) => ({
        url: `/notifications/channels`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          user_id: queryArg.userId,
          device_id: queryArg.deviceId,
        },
      }),
    }),
    postNotificationsTenantChannels: build.mutation<
      PostNotificationsTenantChannelsApiResponse,
      PostNotificationsTenantChannelsApiArg
    >({
      query: (queryArg) => ({
        url: `/notifications/tenant/channels`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getNotificationsEventTypes: build.query<GetNotificationsEventTypesApiResponse, GetNotificationsEventTypesApiArg>({
      query: (queryArg) => ({
        url: `/notifications/event-types`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          category: queryArg.category,
        },
      }),
    }),
    postNotificationsUserPreferences: build.mutation<
      PostNotificationsUserPreferencesApiResponse,
      PostNotificationsUserPreferencesApiArg
    >({
      query: (queryArg) => ({
        url: `/notifications/user-preferences`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getNotificationsUserPreferences: build.query<
      GetNotificationsUserPreferencesApiResponse,
      GetNotificationsUserPreferencesApiArg
    >({
      query: (queryArg) => ({
        url: `/notifications/user-preferences`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          category: queryArg.category,
        },
      }),
    }),
    getScenes: build.query<GetScenesApiResponse, GetScenesApiArg>({
      query: (queryArg) => ({
        url: `/scenes`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          location_id: queryArg.locationId,
          is_active: queryArg.isActive,
          limit: queryArg.limit,
          offset: queryArg.offset,
          sort_by: queryArg.sortBy,
          sort_dir: queryArg.sortDir,
        },
      }),
    }),
    postScenes: build.mutation<PostScenesApiResponse, PostScenesApiArg>({
      query: (queryArg) => ({
        url: `/scenes`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getScenesBySceneId: build.query<GetScenesBySceneIdApiResponse, GetScenesBySceneIdApiArg>({
      query: (queryArg) => ({
        url: `/scenes/${queryArg.sceneId}`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    patchScenesBySceneId: build.mutation<PatchScenesBySceneIdApiResponse, PatchScenesBySceneIdApiArg>({
      query: (queryArg) => ({
        url: `/scenes/${queryArg.sceneId}`,
        method: 'PATCH',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    deleteScenesBySceneId: build.mutation<DeleteScenesBySceneIdApiResponse, DeleteScenesBySceneIdApiArg>({
      query: (queryArg) => ({
        url: `/scenes/${queryArg.sceneId}`,
        method: 'DELETE',
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    postScenesBySceneIdClone: build.mutation<PostScenesBySceneIdCloneApiResponse, PostScenesBySceneIdCloneApiArg>({
      query: (queryArg) => ({
        url: `/scenes/${queryArg.sceneId}/clone`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getScenesBySceneIdObjects: build.query<GetScenesBySceneIdObjectsApiResponse, GetScenesBySceneIdObjectsApiArg>({
      query: (queryArg) => ({
        url: `/scenes/${queryArg.sceneId}/objects`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          asset_id: queryArg.assetId,
          sensor_id: queryArg.sensorId,
          limit: queryArg.limit,
          offset: queryArg.offset,
          sort_by: queryArg.sortBy,
          sort_dir: queryArg.sortDir,
        },
      }),
    }),
    postScenesBySceneIdObjects: build.mutation<PostScenesBySceneIdObjectsApiResponse, PostScenesBySceneIdObjectsApiArg>(
      {
        query: (queryArg) => ({
          url: `/scenes/${queryArg.sceneId}/objects`,
          method: 'POST',
          body: queryArg.body,
          headers: {
            'x-tenant-id': queryArg['x-tenant-id'],
          },
        }),
      },
    ),
    patchScenesBySceneIdObjectsAndObjectId: build.mutation<
      PatchScenesBySceneIdObjectsAndObjectIdApiResponse,
      PatchScenesBySceneIdObjectsAndObjectIdApiArg
    >({
      query: (queryArg) => ({
        url: `/scenes/${queryArg.sceneId}/objects/${queryArg.objectId}`,
        method: 'PATCH',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    deleteScenesBySceneIdObjectsAndObjectId: build.mutation<
      DeleteScenesBySceneIdObjectsAndObjectIdApiResponse,
      DeleteScenesBySceneIdObjectsAndObjectIdApiArg
    >({
      query: (queryArg) => ({
        url: `/scenes/${queryArg.sceneId}/objects/${queryArg.objectId}`,
        method: 'DELETE',
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    postScenesBySceneIdObjectsBatch: build.mutation<
      PostScenesBySceneIdObjectsBatchApiResponse,
      PostScenesBySceneIdObjectsBatchApiArg
    >({
      query: (queryArg) => ({
        url: `/scenes/${queryArg.sceneId}/objects/batch`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getVisualizationAssets: build.query<GetVisualizationAssetsApiResponse, GetVisualizationAssetsApiArg>({
      query: (queryArg) => ({
        url: `/visualization/assets`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          type: queryArg['type'],
          category: queryArg.category,
          is_public: queryArg.isPublic,
          search: queryArg.search,
          limit: queryArg.limit,
          offset: queryArg.offset,
          sort_by: queryArg.sortBy,
          sort_dir: queryArg.sortDir,
        },
      }),
    }),
    postVisualizationAssets: build.mutation<PostVisualizationAssetsApiResponse, PostVisualizationAssetsApiArg>({
      query: (queryArg) => ({
        url: `/visualization/assets`,
        method: 'POST',
        body: queryArg.body,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getVisualizationAssetsByAssetIdDownload: build.query<
      GetVisualizationAssetsByAssetIdDownloadApiResponse,
      GetVisualizationAssetsByAssetIdDownloadApiArg
    >({
      query: (queryArg) => ({
        url: `/visualization/assets/${queryArg.assetId}/download`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    deleteVisualizationAssetsByAssetId: build.mutation<
      DeleteVisualizationAssetsByAssetIdApiResponse,
      DeleteVisualizationAssetsByAssetIdApiArg
    >({
      query: (queryArg) => ({
        url: `/visualization/assets/${queryArg.assetId}`,
        method: 'DELETE',
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
      }),
    }),
    getVisualizationTemplates: build.query<GetVisualizationTemplatesApiResponse, GetVisualizationTemplatesApiArg>({
      query: (queryArg) => ({
        url: `/visualization/templates`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          category: queryArg.category,
          is_public: queryArg.isPublic,
          search: queryArg.search,
          limit: queryArg.limit,
          offset: queryArg.offset,
          sort_by: queryArg.sortBy,
          sort_dir: queryArg.sortDir,
        },
      }),
    }),
    getVisualizationTemplatesByType: build.query<
      GetVisualizationTemplatesByTypeApiResponse,
      GetVisualizationTemplatesByTypeApiArg
    >({
      query: (queryArg) => ({
        url: `/visualization/templates/${queryArg['type']}`,
        headers: {
          'x-tenant-id': queryArg['x-tenant-id'],
        },
        params: {
          category: queryArg.category,
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as Api };
export type PostAuthLoginApiResponse = /** status 200 User */ {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken: string;
  };
};
export type PostAuthLoginApiArg = {
  body: {
    email: string | null;
    password: string;
  };
};
export type PostAuthRefreshApiResponse = /** status 200 New access and refresh tokens */ {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken: string;
  };
};
export type PostAuthRefreshApiArg = {
  body: {
    refreshToken: string;
  };
};
export type PostAuthForgotPasswordApiResponse = /** status 200 Reset email sent */ {
  success: boolean;
};
export type PostAuthForgotPasswordApiArg = {
  body: {
    email: string | null;
  };
};
export type PostAuthResetPasswordApiResponse = /** status 200 Password reset successful */ {
  success: boolean;
};
export type PostAuthResetPasswordApiArg = {
  body: {
    token: string;
    password: string;
  };
};
export type PostUsersApiResponse = /** status 200 User created successfully */ {
  success: boolean;
  data: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    email_verified: boolean;
    role: 'user' | 'admin';
    login_type: 'email_and_password' | 'email_and_password';
    status: 'active' | 'inactive' | 'deleted' | 'deactivated' | 'terminated';
  };
};
export type PostUsersApiArg = {
  body: {
    email: string | null;
    password: string;
    role: 'user' | 'admin';
    first_name: string | null;
    last_name: string | null;
  };
};
export type GetUsersMeApiResponse = /** status 200 Current user profile */ {
  success: boolean;
  data: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    email_verified: boolean;
    role: 'user' | 'admin';
    login_type: 'email_and_password' | 'email_and_password';
    status: 'active' | 'inactive' | 'deleted' | 'deactivated' | 'terminated';
  };
};
export type GetUsersMeApiArg = void;
export type PatchUsersMeApiResponse = /** status 200 Updated user profile */ {
  success: boolean;
  data: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    email_verified: boolean;
    role: 'user' | 'admin';
    login_type: 'email_and_password' | 'email_and_password';
    status: 'active' | 'inactive' | 'deleted' | 'deactivated' | 'terminated';
  };
};
export type PatchUsersMeApiArg = {
  body: {
    first_name: string | null;
    last_name: string | null;
  };
};
export type PostUsersVerificationSendApiResponse = /** status 200 Verification email sent successfully */ {
  success: boolean;
};
export type PostUsersVerificationSendApiArg = void;
export type PostUsersVerificationVerifyApiResponse = /** status 200 Email verified successfully */ {
  success: boolean;
};
export type PostUsersVerificationVerifyApiArg = {
  body: {
    code: string;
  };
};
export type PostThingDestinationPayloadApiResponse = /** status 200 Payload created successfully */ {
  success: boolean;
};
export type PostThingDestinationPayloadApiArg = {
  body: {
    thing_name: string;
    timestamp?: string;
    data: {
      [key: string]: {
        value: number;
        unit?: string;
      };
    };
  };
};
export type PostThingDestinationApiResponse = /** status 200 Confirmation successful */ {
  success: boolean;
};
export type PostThingDestinationApiArg = {
  confirmationToken?: string;
};
export type PostLocationsApiResponse = /** status 200 Location created successfully */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    created_at: (string | null) | string | null;
    updated_at: (string | null) | string | null;
  };
};
export type PostLocationsApiArg = {
  body: {
    name: string;
    description?: string;
    parent_id?: string;
  };
};
export type GetLocationsApiResponse = /** status 200 List of locations */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    created_at: (string | null) | string | null;
    updated_at: (string | null) | string | null;
    sub_locations: {
      id: string;
      name: string;
      description: string | null;
      created_at: (string | null) | string | null;
      updated_at: (string | null) | string | null;
    }[];
  }[];
};
export type GetLocationsApiArg = {
  parentId?: string;
  tenantId?: string;
  includeSubLocations?: 'true' | 'false';
  'x-tenant-id'?: string;
};
export type PostLocationsByParentIdSubApiResponse = /** status 200 Sub-location created successfully */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    created_at: (string | null) | string | null;
    updated_at: (string | null) | string | null;
  };
};
export type PostLocationsByParentIdSubApiArg = {
  parentId: string;
  body: {
    name: string;
    description?: string;
    parent_id?: string;
  };
};
export type GetLocationsByIdApiResponse = /** status 200 Location details */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    created_at: (string | null) | string | null;
    updated_at: (string | null) | string | null;
    sub_locations: {
      id: string;
      name: string;
      description: string | null;
      created_at: (string | null) | string | null;
      updated_at: (string | null) | string | null;
    }[];
  };
};
export type GetLocationsByIdApiArg = {
  id: string;
  includeSubLocations?: 'true' | 'false';
  'x-tenant-id'?: string;
};
export type PatchLocationsByIdApiResponse = /** status 200 Location updated successfully */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    created_at: (string | null) | string | null;
    updated_at: (string | null) | string | null;
  };
};
export type PatchLocationsByIdApiArg = {
  id: string;
  'x-tenant-id'?: string;
  body: {
    name?: string;
    description?: string;
    parent_id?: string;
  };
};
export type DeleteLocationsByIdApiResponse = /** status 200 Location deleted successfully */ {
  success: boolean;
};
export type DeleteLocationsByIdApiArg = {
  id: string;
};
export type GetDevicesApiResponse = /** status 200 List of devices */ {
  success: boolean;
  data: {
    id: string;
    serial_number: string;
    mac_address: string | null;
    name: string | null;
    description: string | null;
    location_id?: string | null;
    location_name?: string | null;
    tenant_id?: string | null;
    alert_count?: number;
    sensors?: {
      id: string;
      name: string;
      type: string | null;
      unit: string | null;
      description: string | null;
    }[];
    created_at: (string | null) | string | null;
    updated_at: (string | null) | string | null;
  }[];
};
export type GetDevicesApiArg = {
  locationId?: string;
  locationIds?: string;
  includeSensors?: 'true' | 'false';
  tenantId?: string;
  'x-tenant-id'?: string;
};
export type PostDevicesApiResponse = /** status 200 Device created successfully */ {
  success: boolean;
  data: {
    id: string;
    serial_number: string;
    mac_address: string | null;
    name: string | null;
    description: string | null;
    location_id: string | null;
    tenant_id: string | null;
    created_at: (string | null) | string | null;
    updated_at: (string | null) | string | null;
  };
};
export type PostDevicesApiArg = {
  'x-tenant-id'?: string;
  body: {
    serial_number: string;
    mac_address?: string;
    name?: string;
    description?: string;
    location_id?: string;
    tenant_id?: string;
  };
};
export type GetDevicesByIdApiResponse = /** status 200 Device details */ {
  success: boolean;
  data: {
    id: string;
    serial_number: string;
    mac_address: string | null;
    name: string | null;
    description: string | null;
    location_id: string | null;
    location_name: string | null;
    tenant_id: string | null;
    alert_count?: number;
    sensors?: {
      id: string;
      name: string;
      type: string;
      unit: string | null;
      description: string | null;
      latest_data?: {
        value: number;
        unit: string | null;
        timestamp: string;
      } | null;
    }[];
    created_at: string;
    updated_at: string;
  };
};
export type GetDevicesByIdApiArg = {
  id: string;
  includeSensors?: 'true' | 'false';
  includeLatestData?: 'true' | 'false';
  tenantId?: string;
  'x-tenant-id'?: string;
};
export type PatchDevicesByIdApiResponse = /** status 200 Device updated successfully */ {
  success: boolean;
  data: {
    id: string;
    serial_number: string;
    mac_address: string | null;
    name: string | null;
    description: string | null;
    location_id: string | null;
    tenant_id: string | null;
    created_at: (string | null) | string | null;
    updated_at: (string | null) | string | null;
  };
};
export type PatchDevicesByIdApiArg = {
  id: string;
  'x-tenant-id'?: string;
  body: {
    mac_address?: string;
    name?: string;
    description?: string;
    location_id?: string;
    tenant_id?: string;
  };
};
export type DeleteDevicesByIdApiResponse = /** status 200 Device deleted successfully */ {
  success: boolean;
};
export type DeleteDevicesByIdApiArg = {
  id: string;
  'x-tenant-id'?: string;
};
export type GetDevicesByIdStatsApiResponse = /** status 200 Device statistics */ {
  success: boolean;
  data: {
    device_id: string;
    device_name: string | null;
    sensor_stats: {
      sensor_id: string;
      sensor_name: string;
      sensor_type: string;
      value: number | null;
      unit: string | null;
      timestamp: string | null;
    }[];
  };
};
export type GetDevicesByIdStatsApiArg = {
  id: string;
  hours?: string;
  aggregation?: 'latest' | 'average' | 'min' | 'max';
  tenantId?: string;
  'x-tenant-id'?: string;
};
export type GetDevicesByDeviceIdSensorsApiResponse = /** status 200 List of sensors */ {
  success: boolean;
  data: {
    id: string;
    device_id: string;
    name: string;
    type: string | null;
    unit: string | null;
    description: string | null;
    created_at: string;
    updated_at: string;
  }[];
};
export type GetDevicesByDeviceIdSensorsApiArg = {
  deviceId: string;
  'x-tenant-id'?: string;
};
export type PostDevicesByDeviceIdSensorsApiResponse = /** status 200 Sensor added successfully */ {
  success: boolean;
  data: {
    id: string;
    device_id: string;
    name: string;
    type: string | null;
    unit: string | null;
    description: string | null;
    created_at: string;
    updated_at: string;
  };
};
export type PostDevicesByDeviceIdSensorsApiArg = {
  deviceId: string;
  'x-tenant-id'?: string;
  body: {
    name: string;
    type: string | null;
    description: string | null;
    unit: string | null;
  };
};
export type PatchDevicesByDeviceIdSensorsAndSensorIdApiResponse = /** status 200 Sensor updated successfully */ {
  success: boolean;
  data: {
    id: string;
    device_id: string;
    name: string;
    type: string | null;
    unit: string | null;
    description: string | null;
    created_at: string;
    updated_at: string;
  };
};
export type PatchDevicesByDeviceIdSensorsAndSensorIdApiArg = {
  deviceId: string;
  sensorId: string;
  'x-tenant-id'?: string;
  body: {
    name?: string;
    type?: string | null;
    description?: string | null;
    unit?: string | null;
  };
};
export type DeleteDevicesByDeviceIdSensorsAndSensorIdApiResponse = /** status 200 Sensor deleted successfully */ {
  success: boolean;
};
export type DeleteDevicesByDeviceIdSensorsAndSensorIdApiArg = {
  deviceId: string;
  sensorId: string;
  'x-tenant-id'?: string;
};
export type GetExportSensorDataApiResponse = /** status 200 Sensor data exported successfully */ string;
export type GetExportSensorDataApiArg = {
  deviceIds?: string;
  sensorTypes?: string;
  locationId?: string;
  locationIds?: string;
  startDate?: string;
  endDate?: string;
  format?: 'csv' | 'json' | 'xlsx' | 'pdf';
  'x-tenant-id'?: string;
};
export type GetExportSensorAggregatesApiResponse = /** status 200 Sensor data exported successfully */ string;
export type GetExportSensorAggregatesApiArg = {
  locationId?: string;
  locationIds?: string;
  deviceId?: string;
  deviceIds?: string;
  startDate?: string;
  endDate?: string;
  sensorTypes?: string;
  interval?: 'minute' | 'hour' | 'day' | 'week' | 'month';
  format?: 'csv' | 'json' | 'xlsx' | 'pdf' | 'chart';
  chartType?: 'line' | 'area' | 'gauge';
  limit?: string;
  offset?: string;
  'x-tenant-id'?: string;
};
export type GetExportSensorTimeSeriesApiResponse = /** status 200 Sensor data exported successfully */ string;
export type GetExportSensorTimeSeriesApiArg = {
  deviceIds?: string;
  sensorTypes?: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
  interval?: 'hour' | 'day' | 'week' | 'month';
  aggregation?: 'avg' | 'min' | 'max' | 'sum' | 'count';
  format?: 'csv' | 'json' | 'xlsx' | 'pdf' | 'chart';
  chartType?: 'line' | 'area' | 'gauge';
  limit?: string;
  tenantId?: string;
  'x-tenant-id'?: string;
};
export type GetAnalyticsDashboardApiResponse = /** status 200 Dashboard statistics retrieved successfully */ {
  success: boolean;
  data: {
    total_devices: number;
    active_devices: number;
    total_sensors: number;
    active_sensors: number;
    readings_last_24h: number;
    recent_alerts: number;
    sensor_type_distribution: {
      type: string;
      count: number;
    }[];
    reading_trends: {
      date: string;
      count: number;
    }[];
  };
};
export type GetAnalyticsDashboardApiArg = {
  days?: string;
  locationId?: string;
  locationIds?: string;
  'x-tenant-id'?: string;
};
export type GetAnalyticsSensorDataApiResponse = /** status 200 Sensor data retrieved successfully */ {
  success: boolean;
  data: {
    id: string;
    deviceId: string;
    deviceName: string | null;
    sensorType: string;
    value: number;
    unit: string | null;
    recordedAt: string;
  }[];
};
export type GetAnalyticsSensorDataApiArg = {
  locationId?: string;
  locationIds?: string;
  deviceId?: string;
  deviceIds?: string;
  startDate?: string;
  endDate?: string;
  sensorTypes?: string;
  limit?: string;
  offset?: string;
  'x-tenant-id'?: string;
};
export type GetAnalyticsSensorAggregatesApiResponse = /** status 200 Aggregated sensor data retrieved successfully */ {
  success: boolean;
  data: {
    timestamp: string;
    sensor_type: string;
    min: number;
    max: number;
    avg: number;
    count: number;
    unit: string | null;
  }[];
};
export type GetAnalyticsSensorAggregatesApiArg = {
  locationId?: string;
  locationIds?: string;
  deviceId?: string;
  deviceIds?: string;
  startDate?: string;
  endDate?: string;
  sensorTypes?: string;
  interval?: 'minute' | 'hour' | 'day' | 'week' | 'month';
  limit?: string;
  offset?: string;
  'x-tenant-id'?: string;
};
export type GetAnalyticsDeviceStatusApiResponse = /** status 200 Device status data retrieved successfully */ {
  success: boolean;
  data: {
    items: {
      id: string;
      name: string | null;
      serial_number: string | null;
      status: 'online' | 'offline' | 'warning' | 'error';
      last_reading_at: string | null;
      uptime_seconds: number | null;
      last_24h_readings: number;
      health_score: number;
      location_id: string | null;
      location_name: string | null;
    }[];
    total: number;
  };
};
export type GetAnalyticsDeviceStatusApiArg = {
  locationId?: string;
  locationIds?: string;
  status?: 'online' | 'offline' | 'warning' | 'error';
  search?: string;
  limit?: string;
  offset?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  'x-tenant-id'?: string;
};
export type GetAnalyticsTimeSeriesApiResponse = /** status 200 Time series data retrieved successfully */ {
  success: boolean;
  data: {
    device_id: string;
    device_name: string | null;
    sensor_id: string;
    sensor_name: string;
    sensor_type: string;
    unit: string | null;
    series: {
      timestamp: string;
      value: number | null;
      count: number;
    }[];
  }[];
};
export type GetAnalyticsTimeSeriesApiArg = {
  deviceIds?: string;
  sensorTypes?: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
  interval?: 'hour' | 'day' | 'week' | 'month';
  aggregation?: 'avg' | 'min' | 'max' | 'sum' | 'count';
  limit?: string;
  tenantId?: string;
  'x-tenant-id'?: string;
};
export type GetTenantsApiResponse = /** status 200 List of tenants */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    type: string;
    description: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  }[];
};
export type GetTenantsApiArg = void;
export type PostTenantsApiResponse = /** status 200 Tenant created successfully */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    type: string;
    description: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  };
};
export type PostTenantsApiArg = {
  body: {
    name: string;
    type?: 'personal' | 'business' | 'enterprise';
    description?: string;
  };
};
export type GetTenantsByIdApiResponse = /** status 200 Tenant details */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    type: string;
    description: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  };
};
export type GetTenantsByIdApiArg = {
  id: string;
};
export type PatchTenantsByIdApiResponse = /** status 200 Tenant updated successfully */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    type: string;
    description: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  };
};
export type PatchTenantsByIdApiArg = {
  id: string;
  body: {
    name?: string;
    type?: 'personal' | 'business' | 'enterprise';
    description?: string;
  };
};
export type DeleteTenantsByIdApiResponse = /** status 200 Tenant deleted successfully */ {
  success: boolean;
};
export type DeleteTenantsByIdApiArg = {
  id: string;
};
export type PostTenantsByIdMembersInviteApiResponse = /** status 200 Invitation sent successfully */ {
  success: boolean;
  data: {
    tenant_id: string;
    tenant_name: string;
    user: {
      id: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
    };
    role: string;
    status: string;
    invitation_token: string;
    invitation_expires_at: string;
  };
};
export type PostTenantsByIdMembersInviteApiArg = {
  id: string;
  body: {
    email: string;
    role?: 'admin' | 'member';
  };
};
export type GetTenantsInvitationsVerifyApiResponse = /** status 200 Invitation verification result */ {
  success: boolean;
  data: {
    valid: boolean;
    expired: boolean;
    already_accepted?: boolean;
    tenant_name: string | null;
    role: string | null;
    requires_login: boolean;
    email: string | null;
    is_user_created?: boolean;
    tenant_id: string | null;
  };
};
export type GetTenantsInvitationsVerifyApiArg = {
  token: string;
};
export type PostTenantsInvitationsAcceptApiResponse = /** status 200 Invitation accepted successfully */ {
  success: boolean;
  data: {
    tenant_id: string;
    tenant_name: string;
    role: string;
  };
};
export type PostTenantsInvitationsAcceptApiArg = {
  body: {
    token: string;
  };
};
export type GetTenantsByIdMembersApiResponse = /** status 200 List of tenant members */ {
  success: boolean;
  data: {
    id: string;
    user_id: string | null;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    role: 'owner' | 'admin' | 'member';
    status: 'active' | 'pending' | 'suspended';
    created_at?: string | null;
  }[];
};
export type GetTenantsByIdMembersApiArg = {
  id: string;
};
export type PatchTenantsByTenantIdMembersAndMemberIdRoleApiResponse =
  /** status 200 Member role updated successfully */ {
    success: boolean;
    data: {
      id: string;
      user_id: string;
      email: string;
      role: string;
    };
  };
export type PatchTenantsByTenantIdMembersAndMemberIdRoleApiArg = {
  tenantId: string;
  memberId: string;
  body: {
    role: 'admin' | 'member';
  };
};
export type DeleteTenantsByTenantIdMembersAndMemberIdApiResponse = /** status 200 Member removed successfully */ {
  success: boolean;
};
export type DeleteTenantsByTenantIdMembersAndMemberIdApiArg = {
  tenantId: string;
  memberId: string;
};
export type PostTenantsByTenantIdMembersAndMemberIdResetPasswordApiResponse = /** status 200 Reset email sent */ {
  success: boolean;
};
export type PostTenantsByTenantIdMembersAndMemberIdResetPasswordApiArg = {
  tenantId: string;
  memberId: string;
};
export type GetAlertRulesApiResponse = /** status 200 Alert rules */ {
  success: boolean;
  data: {
    /** The items */
    items: {
      id: string;
      pk: number;
      name: string;
      description: string | null;
      tenant_pk: number;
      device_pk: number | null;
      notification_event_type_pk: number;
      sensor_type: string | null;
      condition_type: 'gt' | 'lt' | 'eq' | 'range' | 'between' | 'outside';
      threshold_value?: any;
      duration_seconds: number | null;
      cooldown_minutes?: number;
      severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
      status: 'active' | 'paused';
      created_at: string;
      updated_at: string;
      created_by_user_pk: number | null;
      device: {
        id: string | null;
        name: string | null;
        serial_number: string | null;
      } | null;
      notification_event_type: {
        id: string;
        name: string;
        description: string | null;
        code: string;
        category: string;
      } | null;
      created_by_user: {
        id: string | null;
        name: string | null;
      } | null;
    }[];
    /** The total number of items */
    total: number;
  };
};
export type GetAlertRulesApiArg = {
  deviceId?: string;
  deviceIds?: string;
  sensorType?: string;
  notificationEventTypeId?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  status?: 'active' | 'paused';
  limit?: string;
  offset?: string;
  sortBy?:
    | 'id'
    | 'pk'
    | 'name'
    | 'description'
    | 'tenant_pk'
    | 'device_pk'
    | 'notification_event_type_pk'
    | 'sensor_type'
    | 'condition_type'
    | 'threshold_value'
    | 'duration_seconds'
    | 'cooldown_minutes'
    | 'severity'
    | 'status'
    | 'created_at'
    | 'updated_at'
    | 'created_by_user_pk';
  sortDir?: 'asc' | 'desc';
  'x-tenant-id'?: string;
};
export type PostAlertRulesApiResponse = /** status 200 Created */ {
  success: boolean;
  data: {
    id: string;
    pk: number;
    name: string;
    description: string | null;
    tenant_pk: number;
    device_pk: number | null;
    notification_event_type_pk: number;
    sensor_type: string | null;
    condition_type: 'gt' | 'lt' | 'eq' | 'range' | 'between' | 'outside';
    threshold_value?: any;
    duration_seconds: number | null;
    cooldown_minutes?: number;
    severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
    status: 'active' | 'paused';
    created_at: string;
    updated_at: string;
    created_by_user_pk: number | null;
    device: {
      id: string | null;
      name: string | null;
      serial_number: string | null;
    } | null;
    notification_event_type: {
      id: string;
      name: string;
      description: string | null;
      code: string;
      category: string;
    } | null;
    created_by_user: {
      id: string | null;
      name: string | null;
    } | null;
  };
};
export type PostAlertRulesApiArg = {
  'x-tenant-id'?: string;
  body: {
    name: string;
    description: string | null;
    sensor_type: string | null;
    condition_type: 'gt' | 'lt' | 'eq' | 'range' | 'between' | 'outside';
    threshold_value?: any;
    duration_seconds: number | null;
    cooldown_minutes?: number;
    severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
    status: 'active' | 'paused';
    device_id: string;
    notification_event_type_id: string;
  };
};
export type PutAlertRulesByRuleIdApiResponse = /** status 200 Updated */ {
  success: boolean;
  data: {
    id: string;
    pk: number;
    name: string;
    description: string | null;
    tenant_pk: number;
    device_pk: number | null;
    notification_event_type_pk: number;
    sensor_type: string | null;
    condition_type: 'gt' | 'lt' | 'eq' | 'range' | 'between' | 'outside';
    threshold_value?: any;
    duration_seconds: number | null;
    cooldown_minutes?: number;
    severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
    status: 'active' | 'paused';
    created_at: string;
    updated_at: string;
    created_by_user_pk: number | null;
    device: {
      id: string | null;
      name: string | null;
      serial_number: string | null;
    } | null;
    notification_event_type: {
      id: string;
      name: string;
      description: string | null;
      code: string;
      category: string;
    } | null;
    created_by_user: {
      id: string | null;
      name: string | null;
    } | null;
  };
};
export type PutAlertRulesByRuleIdApiArg = {
  ruleId: string;
  'x-tenant-id'?: string;
  body: {
    name?: string;
    description?: string | null;
    sensor_type?: string | null;
    condition_type?: 'gt' | 'lt' | 'eq' | 'range' | 'between' | 'outside';
    threshold_value?: any;
    duration_seconds?: number | null;
    cooldown_minutes?: number;
    severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
    status?: 'active' | 'paused';
    device_id?: string;
    notification_event_type_id?: string;
  };
};
export type DeleteAlertRulesByRuleIdApiResponse = /** status 200 Deleted */ {
  success: boolean;
  data: boolean;
};
export type DeleteAlertRulesByRuleIdApiArg = {
  ruleId: string;
  'x-tenant-id'?: string;
};
export type GetAlertsApiResponse = /** status 200 Alerts */ {
  success: boolean;
  data: {
    /** The items */
    items: {
      id: string;
      pk: number;
      alert_rule_pk: number;
      tenant_pk: number;
      device_pk: number;
      sensor_pk: number;
      status: 'active' | 'acknowledged' | 'resolved';
      triggered_value?: any;
      triggered_at: string;
      acknowledged_at: string | null;
      acknowledged_by_user_pk: number | null;
      resolved_at: string | null;
      note: string | null;
      severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
      created_at: string;
      updated_at: string;
      device: {
        pk: number;
        id: string;
        serial_number: string;
        mac_address: string | null;
        name: string | null;
        description: string | null;
        created_at: (string | null) | string | null;
        updated_at: (string | null) | string | null;
        deleted_at: (string | null) | string | null;
      } | null;
      sensor: {
        id: string;
        name: string;
        type: string | null;
        unit: string | null;
      } | null;
      alert_rule: {
        id: string;
        name: string;
        description: string | null;
        condition_type: 'gt' | 'lt' | 'eq' | 'range' | 'between' | 'outside';
        threshold_value?: any;
        severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
      } | null;
      acknowledged_by_user: {
        id: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
      } | null;
    }[];
    /** The total number of items */
    total: number;
  };
};
export type GetAlertsApiArg = {
  deviceId?: string;
  deviceIds?: string;
  sensorId?: string;
  sensorIds?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  status?: 'active' | 'acknowledged' | 'resolved';
  fromDate?: string;
  toDate?: string;
  includeRule?: 'true' | 'false';
  limit?: string;
  offset?: string;
  sortBy?:
    | 'id'
    | 'pk'
    | 'alert_rule_pk'
    | 'tenant_pk'
    | 'device_pk'
    | 'sensor_pk'
    | 'status'
    | 'triggered_value'
    | 'triggered_at'
    | 'acknowledged_at'
    | 'acknowledged_by_user_pk'
    | 'resolved_at'
    | 'note'
    | 'severity'
    | 'created_at'
    | 'updated_at';
  sortDir?: 'asc' | 'desc';
  'x-tenant-id'?: string;
};
export type GetAlertsHistoryApiResponse = /** status 200 Alert history */ {
  success: boolean;
  data: {
    /** The items */
    items: {
      id: string;
      pk: number;
      alert_rule_pk: number;
      tenant_pk: number;
      device_pk: number;
      sensor_pk: number;
      status: 'active' | 'acknowledged' | 'resolved';
      triggered_value?: any;
      triggered_at: string;
      acknowledged_at: string | null;
      acknowledged_by_user_pk: number | null;
      resolved_at: string | null;
      note: string | null;
      severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
      created_at: string;
      updated_at: string;
      device: {
        pk: number;
        id: string;
        serial_number: string;
        mac_address: string | null;
        name: string | null;
        description: string | null;
        created_at: (string | null) | string | null;
        updated_at: (string | null) | string | null;
        deleted_at: (string | null) | string | null;
      } | null;
      sensor: {
        id: string;
        name: string;
        type: string | null;
        unit: string | null;
      } | null;
      alert_rule: {
        id: string;
        name: string;
        description: string | null;
        condition_type: 'gt' | 'lt' | 'eq' | 'range' | 'between' | 'outside';
        threshold_value?: any;
        severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
      } | null;
      acknowledged_by_user: {
        id: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
      } | null;
    }[];
    /** The total number of items */
    total: number;
  };
};
export type GetAlertsHistoryApiArg = {
  deviceId?: string;
  deviceIds?: string;
  sensorId?: string;
  sensorIds?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  status?: 'active' | 'acknowledged' | 'resolved';
  fromDate?: string;
  toDate?: string;
  includeRule?: 'true' | 'false';
  limit?: string;
  offset?: string;
  sortBy?:
    | 'id'
    | 'pk'
    | 'alert_rule_pk'
    | 'tenant_pk'
    | 'device_pk'
    | 'sensor_pk'
    | 'status'
    | 'triggered_value'
    | 'triggered_at'
    | 'acknowledged_at'
    | 'acknowledged_by_user_pk'
    | 'resolved_at'
    | 'note'
    | 'severity'
    | 'created_at'
    | 'updated_at';
  sortDir?: 'asc' | 'desc';
  'x-tenant-id'?: string;
};
export type PostAlertsByAlertIdAcknowledgeApiResponse = /** status 200 Acknowledged */ {
  success: boolean;
};
export type PostAlertsByAlertIdAcknowledgeApiArg = {
  alertId: string;
  'x-tenant-id'?: string;
  body: {
    note?: string;
  };
};
export type PostAlertsByAlertIdResolveApiResponse = /** status 200 Resolved */ {
  success: boolean;
};
export type PostAlertsByAlertIdResolveApiArg = {
  alertId: string;
  'x-tenant-id'?: string;
  body: {
    note?: string;
  };
};
export type GetWaitlistCountApiResponse = /** status 200 Waitlist count */ {
  success: boolean;
  data: {
    waitlistCount: number;
  };
};
export type GetWaitlistCountApiArg = void;
export type PostWaitlistJoinApiResponse = /** status 200 Successfully joined waitlist */ {
  success: boolean;
  data: {
    waitlistCount: number;
  };
};
export type PostWaitlistJoinApiArg = {
  body: {
    email: string;
    name?: string;
  };
};
export type PostAiChatbotApiResponse = /** status 200 Created */ {
  success: boolean;
  data: {
    response: string;
    sessionId: string;
    providerUsed: string;
    attachments: {
      type: string;
      data?: any;
    }[];
    questions?: string[];
    errors?: string[];
  };
};
export type PostAiChatbotApiArg = {
  'x-tenant-id'?: string;
  body: {
    message: string;
    conversationId?: string;
  };
};
export type GetAiChatbotHistoryApiResponse = /** status 200 Chat session history retrieved successfully */ {
  success: boolean;
  data: {
    /** The items */
    items: {
      id: string;
      type: string;
      content: string;
      timestamp: string;
      attachments: {
        type: string;
        data?: any;
      }[];
    }[];
    /** The total number of items */
    total: number;
  };
};
export type GetAiChatbotHistoryApiArg = {
  limit?: string;
  offset?: string;
  'x-tenant-id'?: string;
};
export type PostNotificationsChannelsApiResponse = /** status 200 Configuration applied succesfully */ {
  success: boolean;
};
export type PostNotificationsChannelsApiArg = {
  'x-tenant-id'?: string;
  body: {
    device_id?: string;
    channels: {
      name: string;
      type: 'email' | 'sms' | 'push';
      status: 'active' | 'paused';
      config: {
        critical?: boolean;
        high?: boolean;
        medium?: boolean;
        low?: boolean;
        info?: boolean;
      };
    }[];
  };
};
export type GetNotificationsChannelsApiResponse = /** status 200 List of notification channels */ {
  success: boolean;
  data: {
    name: string;
    type: 'email' | 'sms' | 'push';
    status: 'active' | 'paused';
    config: {
      critical: boolean;
      high: boolean;
      medium: boolean;
      low: boolean;
      info: boolean;
    };
    tenant_pk: number;
    user_pk?: number | null;
    device_pk?: number | null;
    id: string | null;
    pk: number | null;
    deleted_at: string | null;
    created_at: string | null;
    updated_at: string | null;
  }[];
};
export type GetNotificationsChannelsApiArg = {
  userId?: string;
  deviceId?: string;
  'x-tenant-id'?: string;
};
export type PostNotificationsTenantChannelsApiResponse = /** status 200 Configuration applied succesfully */ {
  success: boolean;
};
export type PostNotificationsTenantChannelsApiArg = {
  'x-tenant-id'?: string;
  body: {
    channels: {
      name: string;
      type: 'email' | 'sms' | 'push';
      status: 'active' | 'paused';
      config: {
        critical?: boolean;
        high?: boolean;
        medium?: boolean;
        low?: boolean;
        info?: boolean;
      };
    }[];
  };
};
export type GetNotificationsEventTypesApiResponse = /** status 200 List of devices */ {
  success: boolean;
  data?: any;
};
export type GetNotificationsEventTypesApiArg = {
  category?: string;
  'x-tenant-id'?: string;
};
export type PostNotificationsUserPreferencesApiResponse = /** status 200 Configuration applied succesfully */ {
  success: boolean;
};
export type PostNotificationsUserPreferencesApiArg = {
  'x-tenant-id'?: string;
  body: {
    event_type_id: string;
    enabled: boolean;
    channels: {
      email: boolean;
      sms: boolean;
      push: boolean;
      webapp: boolean;
    };
    quiet_hours_start?: string | null;
    quiet_hours_end?: string | null;
    quiet_hours_timezone?: string | null;
  }[];
};
export type GetNotificationsUserPreferencesApiResponse = /** status 200 List of devices */ {
  success: boolean;
  data: {
    pk: number;
    user_pk: number;
    tenant_pk: number;
    event_type_pk: number;
    deleted_at: string | null;
    created_at: string;
    updated_at: string;
    enabled: boolean;
    channels: {
      sms: boolean;
      push: boolean;
      email: boolean;
      webapp: boolean;
    };
    quiet_hours_start: string | null;
    quiet_hours_end: string | null;
    quiet_hours_timezone: string | null;
    event_type_id: string;
    event_type_name: string;
    event_type_code: string;
    event_type_description: string;
    event_type_category: string;
    event_type_is_default_enabled: boolean;
  }[];
};
export type GetNotificationsUserPreferencesApiArg = {
  category?: string;
  'x-tenant-id'?: string;
};
export type GetScenesApiResponse = /** status 200 Scenes */ {
  success: boolean;
  data: {
    /** The items */
    items: {
      id: string;
      name: string;
      description: string | null;
      location_id: string;
      is_active: boolean;
      camera_position: {
        x: number;
        y: number;
        z: number;
      };
      camera_target: {
        x: number;
        y: number;
        z: number;
      };
      created_at: string;
      updated_at: string;
      objects?: any[];
    }[];
    /** The total number of items */
    total: number;
  };
};
export type GetScenesApiArg = {
  locationId?: string;
  isActive?: boolean;
  limit?: string;
  offset?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  'x-tenant-id'?: string;
};
export type PostScenesApiResponse = /** status 200 Created */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    location_id: string;
    is_active: boolean;
    camera_position: {
      x: number;
      y: number;
      z: number;
    };
    camera_target: {
      x: number;
      y: number;
      z: number;
    };
    created_at: string;
    updated_at: string;
    objects?: any[];
  };
};
export type PostScenesApiArg = {
  'x-tenant-id'?: string;
  body: {
    name: string;
    description?: string;
    location_id: string;
    camera_position: {
      x: number;
      y: number;
      z: number;
    };
    camera_target: {
      x: number;
      y: number;
      z: number;
    };
  };
};
export type GetScenesBySceneIdApiResponse = /** status 200 Scene details */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    location_id: string;
    is_active: boolean;
    camera_position: {
      x: number;
      y: number;
      z: number;
    };
    camera_target: {
      x: number;
      y: number;
      z: number;
    };
    created_at: string;
    updated_at: string;
    objects?: any[];
  };
};
export type GetScenesBySceneIdApiArg = {
  sceneId: string;
  'x-tenant-id'?: string;
};
export type PatchScenesBySceneIdApiResponse = /** status 200 Updated */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    location_id: string;
    is_active: boolean;
    camera_position: {
      x: number;
      y: number;
      z: number;
    };
    camera_target: {
      x: number;
      y: number;
      z: number;
    };
    created_at: string;
    updated_at: string;
    objects?: any[];
  };
};
export type PatchScenesBySceneIdApiArg = {
  sceneId: string;
  'x-tenant-id'?: string;
  body: {
    name?: string;
    description?: string;
    is_active?: boolean;
    camera_position?: {
      x: number;
      y: number;
      z: number;
    };
    camera_target?: {
      x: number;
      y: number;
      z: number;
    };
  };
};
export type DeleteScenesBySceneIdApiResponse = /** status 200 Deleted */ {
  success: boolean;
};
export type DeleteScenesBySceneIdApiArg = {
  sceneId: string;
  'x-tenant-id'?: string;
};
export type PostScenesBySceneIdCloneApiResponse = /** status 200 Cloned */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string | null;
    location_id: string;
    is_active: boolean;
    camera_position: {
      x: number;
      y: number;
      z: number;
    };
    camera_target: {
      x: number;
      y: number;
      z: number;
    };
    created_at: string;
    updated_at: string;
    objects?: any[];
  };
};
export type PostScenesBySceneIdCloneApiArg = {
  sceneId: string;
  'x-tenant-id'?: string;
  body: {
    name: string;
    description?: string;
  };
};
export type GetScenesBySceneIdObjectsApiResponse = /** status 200 Scene objects */ {
  success: boolean;
  data: {
    /** The items */
    items: {
      id: string;
      scene_id: string;
      asset_id: string;
      name: string;
      position: {
        x: number;
        y: number;
        z: number;
      };
      rotation: {
        x: number;
        y: number;
        z: number;
      };
      scale: {
        x: number;
        y: number;
        z: number;
      };
      sensor_id: string | null;
      metadata?: {
        [key: string]: any;
      };
      created_at: string;
      updated_at: string;
    }[];
    /** The total number of items */
    total: number;
  };
};
export type GetScenesBySceneIdObjectsApiArg = {
  sceneId: string;
  assetId?: string;
  sensorId?: string;
  limit?: string;
  offset?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  'x-tenant-id'?: string;
};
export type PostScenesBySceneIdObjectsApiResponse = /** status 200 Object added */ {
  success: boolean;
  data: {
    id: string;
    scene_id: string;
    asset_id: string;
    name: string;
    position: {
      x: number;
      y: number;
      z: number;
    };
    rotation: {
      x: number;
      y: number;
      z: number;
    };
    scale: {
      x: number;
      y: number;
      z: number;
    };
    sensor_id: string | null;
    metadata?: {
      [key: string]: any;
    };
    created_at: string;
    updated_at: string;
  };
};
export type PostScenesBySceneIdObjectsApiArg = {
  sceneId: string;
  'x-tenant-id'?: string;
  body: {
    asset_id: string;
    name: string;
    position: {
      x: number;
      y: number;
      z: number;
    };
    rotation?: {
      x: number;
      y: number;
      z: number;
    };
    scale?: {
      x: number;
      y: number;
      z: number;
    };
    sensor_id?: string;
    metadata?: {
      [key: string]: any;
    };
  };
};
export type PatchScenesBySceneIdObjectsAndObjectIdApiResponse = /** status 200 Object updated */ {
  success: boolean;
  data: {
    id: string;
    scene_id: string;
    asset_id: string;
    name: string;
    position: {
      x: number;
      y: number;
      z: number;
    };
    rotation: {
      x: number;
      y: number;
      z: number;
    };
    scale: {
      x: number;
      y: number;
      z: number;
    };
    sensor_id: string | null;
    metadata?: {
      [key: string]: any;
    };
    created_at: string;
    updated_at: string;
  };
};
export type PatchScenesBySceneIdObjectsAndObjectIdApiArg = {
  sceneId: string;
  objectId: string;
  'x-tenant-id'?: string;
  body: {
    name?: string;
    position?: {
      x: number;
      y: number;
      z: number;
    };
    rotation?: {
      x: number;
      y: number;
      z: number;
    };
    scale?: {
      x: number;
      y: number;
      z: number;
    };
    sensor_id?: string;
    metadata?: {
      [key: string]: any;
    };
  };
};
export type DeleteScenesBySceneIdObjectsAndObjectIdApiResponse = /** status 200 Object removed */ {
  success: boolean;
};
export type DeleteScenesBySceneIdObjectsAndObjectIdApiArg = {
  sceneId: string;
  objectId: string;
  'x-tenant-id'?: string;
};
export type PostScenesBySceneIdObjectsBatchApiResponse = /** status 200 Batch operations completed */ {
  success: boolean;
  data: {
    success_count: number;
    error_count: number;
    results: any[];
  };
};
export type PostScenesBySceneIdObjectsBatchApiArg = {
  sceneId: string;
  'x-tenant-id'?: string;
  body: {
    operations: {
      type: 'create' | 'update' | 'delete';
      object_id?: string;
      data?: any;
    }[];
  };
};
export type GetVisualizationAssetsApiResponse = /** status 200 Assets */ {
  success: boolean;
  data: {
    /** The items */
    items: {
      id: string;
      name: string;
      type: 'model' | 'texture' | 'material' | 'scene';
      category: string;
      file_path: string;
      file_size: number;
      mime_type: string;
      thumbnail_path: string | null;
      metadata?: {
        [key: string]: any;
      };
      is_public: boolean;
      created_at: string;
      updated_at: string;
    }[];
    /** The total number of items */
    total: number;
  };
};
export type GetVisualizationAssetsApiArg = {
  type?: 'model' | 'texture' | 'material' | 'scene';
  category?: string;
  isPublic?: boolean;
  search?: string;
  limit?: string;
  offset?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  'x-tenant-id'?: string;
};
export type PostVisualizationAssetsApiResponse = /** status 200 Asset uploaded */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    type: 'model' | 'texture' | 'material' | 'scene';
    category: string;
    file_path: string;
    file_size: number;
    mime_type: string;
    thumbnail_path: string | null;
    metadata?: {
      [key: string]: any;
    };
    is_public: boolean;
    created_at: string;
    updated_at: string;
  };
};
export type PostVisualizationAssetsApiArg = {
  'x-tenant-id'?: string;
  body: {
    file?: any;
    metadata?: string;
  };
};
export type GetVisualizationAssetsByAssetIdDownloadApiResponse = /** status 200 Asset file */ any;
export type GetVisualizationAssetsByAssetIdDownloadApiArg = {
  assetId: string;
  'x-tenant-id'?: string;
};
export type DeleteVisualizationAssetsByAssetIdApiResponse = /** status 200 Asset deleted */ {
  success: boolean;
};
export type DeleteVisualizationAssetsByAssetIdApiArg = {
  assetId: string;
  'x-tenant-id'?: string;
};
export type GetVisualizationTemplatesApiResponse = /** status 200 Templates */ {
  success: boolean;
  data: {
    /** The items */
    items: {
      id: string;
      name: string;
      description: string;
      type: 'scene' | 'object' | 'layout';
      category: string;
      preview_image: string | null;
      config: {
        [key: string]: any;
      };
      is_public: boolean;
      created_at: string;
      updated_at: string;
    }[];
    /** The total number of items */
    total: number;
  };
};
export type GetVisualizationTemplatesApiArg = {
  category?: string;
  isPublic?: boolean;
  search?: string;
  limit?: string;
  offset?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  'x-tenant-id'?: string;
};
export type GetVisualizationTemplatesByTypeApiResponse = /** status 200 Templates by type */ {
  success: boolean;
  data: {
    id: string;
    name: string;
    description: string;
    type: 'scene' | 'object' | 'layout';
    category: string;
    preview_image: string | null;
    config: {
      [key: string]: any;
    };
    is_public: boolean;
    created_at: string;
    updated_at: string;
  }[];
};
export type GetVisualizationTemplatesByTypeApiArg = {
  type: 'scene' | 'object' | 'layout';
  category?: string;
  limit?: string;
  offset?: string;
  'x-tenant-id'?: string;
};
export const {
  usePostAuthLoginMutation,
  usePostAuthRefreshMutation,
  usePostAuthForgotPasswordMutation,
  usePostAuthResetPasswordMutation,
  usePostUsersMutation,
  useGetUsersMeQuery,
  useLazyGetUsersMeQuery,
  usePatchUsersMeMutation,
  usePostUsersVerificationSendMutation,
  usePostUsersVerificationVerifyMutation,
  usePostThingDestinationPayloadMutation,
  usePostThingDestinationMutation,
  usePostLocationsMutation,
  useGetLocationsQuery,
  useLazyGetLocationsQuery,
  usePostLocationsByParentIdSubMutation,
  useGetLocationsByIdQuery,
  useLazyGetLocationsByIdQuery,
  usePatchLocationsByIdMutation,
  useDeleteLocationsByIdMutation,
  useGetDevicesQuery,
  useLazyGetDevicesQuery,
  usePostDevicesMutation,
  useGetDevicesByIdQuery,
  useLazyGetDevicesByIdQuery,
  usePatchDevicesByIdMutation,
  useDeleteDevicesByIdMutation,
  useGetDevicesByIdStatsQuery,
  useLazyGetDevicesByIdStatsQuery,
  useGetDevicesByDeviceIdSensorsQuery,
  useLazyGetDevicesByDeviceIdSensorsQuery,
  usePostDevicesByDeviceIdSensorsMutation,
  usePatchDevicesByDeviceIdSensorsAndSensorIdMutation,
  useDeleteDevicesByDeviceIdSensorsAndSensorIdMutation,
  useGetExportSensorDataQuery,
  useLazyGetExportSensorDataQuery,
  useGetExportSensorAggregatesQuery,
  useLazyGetExportSensorAggregatesQuery,
  useGetExportSensorTimeSeriesQuery,
  useLazyGetExportSensorTimeSeriesQuery,
  useGetAnalyticsDashboardQuery,
  useLazyGetAnalyticsDashboardQuery,
  useGetAnalyticsSensorDataQuery,
  useLazyGetAnalyticsSensorDataQuery,
  useGetAnalyticsSensorAggregatesQuery,
  useLazyGetAnalyticsSensorAggregatesQuery,
  useGetAnalyticsDeviceStatusQuery,
  useLazyGetAnalyticsDeviceStatusQuery,
  useGetAnalyticsTimeSeriesQuery,
  useLazyGetAnalyticsTimeSeriesQuery,
  useGetTenantsQuery,
  useLazyGetTenantsQuery,
  usePostTenantsMutation,
  useGetTenantsByIdQuery,
  useLazyGetTenantsByIdQuery,
  usePatchTenantsByIdMutation,
  useDeleteTenantsByIdMutation,
  usePostTenantsByIdMembersInviteMutation,
  useGetTenantsInvitationsVerifyQuery,
  useLazyGetTenantsInvitationsVerifyQuery,
  usePostTenantsInvitationsAcceptMutation,
  useGetTenantsByIdMembersQuery,
  useLazyGetTenantsByIdMembersQuery,
  usePatchTenantsByTenantIdMembersAndMemberIdRoleMutation,
  useDeleteTenantsByTenantIdMembersAndMemberIdMutation,
  usePostTenantsByTenantIdMembersAndMemberIdResetPasswordMutation,
  useGetAlertRulesQuery,
  useLazyGetAlertRulesQuery,
  usePostAlertRulesMutation,
  usePutAlertRulesByRuleIdMutation,
  useDeleteAlertRulesByRuleIdMutation,
  useGetAlertsQuery,
  useLazyGetAlertsQuery,
  useGetAlertsHistoryQuery,
  useLazyGetAlertsHistoryQuery,
  usePostAlertsByAlertIdAcknowledgeMutation,
  usePostAlertsByAlertIdResolveMutation,
  useGetWaitlistCountQuery,
  useLazyGetWaitlistCountQuery,
  usePostWaitlistJoinMutation,
  usePostAiChatbotMutation,
  useGetAiChatbotHistoryQuery,
  useLazyGetAiChatbotHistoryQuery,
  usePostNotificationsChannelsMutation,
  useGetNotificationsChannelsQuery,
  useLazyGetNotificationsChannelsQuery,
  usePostNotificationsTenantChannelsMutation,
  useGetNotificationsEventTypesQuery,
  useLazyGetNotificationsEventTypesQuery,
  usePostNotificationsUserPreferencesMutation,
  useGetNotificationsUserPreferencesQuery,
  useLazyGetNotificationsUserPreferencesQuery,
  useGetScenesQuery,
  useLazyGetScenesQuery,
  usePostScenesMutation,
  useGetScenesBySceneIdQuery,
  useLazyGetScenesBySceneIdQuery,
  usePatchScenesBySceneIdMutation,
  useDeleteScenesBySceneIdMutation,
  usePostScenesBySceneIdCloneMutation,
  useGetScenesBySceneIdObjectsQuery,
  useLazyGetScenesBySceneIdObjectsQuery,
  usePostScenesBySceneIdObjectsMutation,
  usePatchScenesBySceneIdObjectsAndObjectIdMutation,
  useDeleteScenesBySceneIdObjectsAndObjectIdMutation,
  usePostScenesBySceneIdObjectsBatchMutation,
  useGetVisualizationAssetsQuery,
  useLazyGetVisualizationAssetsQuery,
  usePostVisualizationAssetsMutation,
  useGetVisualizationAssetsByAssetIdDownloadQuery,
  useLazyGetVisualizationAssetsByAssetIdDownloadQuery,
  useDeleteVisualizationAssetsByAssetIdMutation,
  useGetVisualizationTemplatesQuery,
  useLazyGetVisualizationTemplatesQuery,
  useGetVisualizationTemplatesByTypeQuery,
  useLazyGetVisualizationTemplatesByTypeQuery,
} = injectedRtkApi;
